import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { BaseButton } from '@/components/uiKit/Button';
import { FieldValues, useForm } from 'react-hook-form';
import { AvatarSection } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/AvatarSection';
import { Skeleton } from '@/components/ui/skeleton';
import { ProfileSection } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/ProfileSection';
import { RoleSection } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/RoleSection';
import { PasswordSection } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/PasswordSection';
import { useUserDetailsFormMode } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/useUserDetailsFormMode';
import { useUserData } from '@/components/pages/SettingsPage/MainContent/shared/useUserData';
import { UserPreferencesRef } from '@/components/pages/SettingsPage/MainContent/UserManagement/UserPreferences';
import { useTranslation } from 'react-i18next';

export type UserProfileFormData = {
  photo: string | null;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string;
  role: string;
  password: string;
  confirmPassword: string;
};

export interface UserDetailsFormProps {
  userData: any;
  isLoadingUserData: boolean;
}

export const UserDetailsForm = forwardRef<UserPreferencesRef, UserDetailsFormProps>(
  ({ userData, isLoadingUserData }: UserDetailsFormProps, ref) => {
    const { t } = useTranslation();
    const { createUser, updateUser, isCreatingUser, isUpdatingUser } = useUserData();
    const { watch, control, handleSubmit, reset } = useForm<UserProfileFormData>();
    const { photo, full_name: fullName, name_initials: nameInitials } = userData || {};
    const [avatar, setAvatar] = useState<any>(photo ? `data:image/png;base64,${photo}` : '');

    const { isAccountSettingsMode, isCreateUserMode, isEditUserMode } = useUserDetailsFormMode();

    useEffect(() => {
      if (userData) {
        reset({
          ...userData,
          phone_number: userData.phone_number ?? '',
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const onSubmit = async (values: FieldValues) => {

      if (isCreateUserMode) {
        await createUser({ ...values });
      }

      if (isAccountSettingsMode || isEditUserMode) {
        await updateUser({ ...values });
      }
    };

    const handleSaveChangesClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      handleSubmit(onSubmit)();
    };

    useImperativeHandle(ref, () => ({
      triggerSave: () => {
        handleSubmit(onSubmit)();
      },
    }));

    if (isLoadingUserData) {
      return <Skeleton className="h-full" />;
    }

    return (
      <form>
        <div className="flex flex-col h-full w-full gap-4">
          <div className="flex flex-row w-full justify-between items-start">
            <AvatarSection avatar={avatar} setAvatar={setAvatar} fullName={fullName} nameInitials={nameInitials} />

            {isAccountSettingsMode || isCreateUserMode ? (
              <BaseButton variant="filled" size="xl" onClick={handleSaveChangesClick}>
                {isCreatingUser || isUpdatingUser ? t('userDetailsForm.loading') : t('userDetailsForm.saveChanges')}
              </BaseButton>
            ) : null}
          </div>

          <ProfileSection control={control} />
          {isAccountSettingsMode ? null : <RoleSection control={control} />}
          <PasswordSection control={control} watch={watch} />
        </div>
      </form>
    );
  }
);

UserDetailsForm.displayName = 'UserDetailsForm';
